import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { HiOutlineDownload } from "react-icons/hi";
import FinoDmtService from "../../../services/FinoDmtService";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import DataTableComponent from "react-data-table-component";
import SideBar from "../../../components/Sidebar";
import Modal from "react-modal";

// Set modal app element for accessibility
if (typeof window !== "undefined") {
  Modal.setAppElement("#root");
}

const ClaimRefund = () => {
  const today = new Date().toISOString().split("T")[0];

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [searchText, setSearchText] = useState("");
  const [isInitState, setIsInitState] = useState(true);
  const [size, setSize] = useState(10);
  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // OTP Modal States
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [mobileNumber, setMobileNumber] = useState("");
  const [orderId, setOrderId] = useState("");
  const [token, setToken] = useState("");
  const [isGeneratingOtp, setIsGeneratingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [canResendOtp, setCanResendOtp] = useState(false);

  useEffect(() => {
    fetchData();
  }, [page, size]);

  // OTP Timer Effect
  useEffect(() => {
    let timer;
    if (otpTimer > 0) {
      timer = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev <= 1) {
            setCanResendOtp(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [otpTimer]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await FinoDmtService.fundTransferHistory(
        fromDate,
        toDate,
        searchText,
        isInitState,
        true,
        size,
        page,
      );

      if (!result.success) {
        Swal.fire("Oops!", result.message, "error");
        return;
      }

      const responseData = result.data;
      if (responseData.status === "SUCCESS") {
        setData(responseData.data.result);
        setTotalRows(responseData.data.total_count || 0);
      } else {
        Swal.fire("Oops!", responseData.message, "error");
      }
    } catch (error) {
      Swal.fire("Oops!", "Request Failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const refundGenerateOtpHandler = async (orderId) => {
    setOrderId(orderId);
    setIsGeneratingOtp(true);

    try {
      // Call API to generate OTP
      const result = await FinoDmtService.refundGenerateOtp(orderId);

      if (result.success && result.data?.status === "SUCCESS") {
        // Show OTP modal
        setToken(result.data?.data?.token);
        setShowOtpModal(true);
        setOtpTimer(30); // 30 seconds timer
        setCanResendOtp(false);
        Swal.fire(
          "Success!",
          result.data?.message || "OTP sent to sender mobile number",
          "success",
        );
      } else {
        Swal.fire(
          "Error!",
          result.message || "Failed to generate OTP",
          "error",
        );
      }
    } catch (error) {
      Swal.fire("Error!", error.message || "Network error", "error");
    } finally {
      setIsGeneratingOtp(false);
    }
  };

  const handleOtpChange = (elementIndex, event) => {
    const value = event.target.value;

    // Allow only numbers
    if (value && !/^\d+$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[elementIndex] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && elementIndex < 5) {
      const nextInput = document.getElementById(
        `otp-input-${elementIndex + 1}`,
      );
      if (nextInput) nextInput.focus();
    }

    // Auto verify if all fields are filled
    if (newOtp.every((digit) => digit !== "") && newOtp.length === 6) {
      // Wait a bit before auto-verifying
      setTimeout(() => {
        verifyOtpHandler(newOtp.join(""));
      }, 300);
    }
  };

  const handleKeyDown = (elementIndex, event) => {
    // Handle backspace
    if (event.key === "Backspace") {
      const newOtp = [...otp];

      if (!otp[elementIndex] && elementIndex > 0) {
        // Focus previous input if current is empty
        const prevInput = document.getElementById(
          `otp-input-${elementIndex - 1}`,
        );
        if (prevInput) prevInput.focus();
        newOtp[elementIndex - 1] = "";
      } else {
        newOtp[elementIndex] = "";
      }

      setOtp(newOtp);
    }
  };

  const verifyOtpHandler = async (enteredOtp = null) => {
    const otpValue = enteredOtp || otp.join("");

    if (otpValue.length !== 6) {
      Swal.fire("Error!", "Please enter a valid 6-digit OTP", "error");
      return;
    }

    setIsVerifyingOtp(true);

    try {
      // Call API to verify OTP and process refund
      const result = await FinoDmtService.refundVerifyOtp(
        orderId,
        token,
        otpValue,
      );

      if (result.success && result.data?.status === "SUCCESS") {
        Swal.fire({
          title: "Success!",
          text: result.data.message || "Refund initiated successfully",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          // Close modal and refresh data
          setShowOtpModal(false);
          resetOtpModal();
          fetchData(); // Refresh the table data
        });
      } else {
        Swal.fire(
          "Error!",
          result.message || "OTP verification failed",
          "error",
        );
      }
    } catch (error) {
      Swal.fire("Error!", error.message || "Network error", "error");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const resendOtpHandler = async () => {
    if (!canResendOtp) return;

    setIsGeneratingOtp(true);

    try {
      const result = await FinoDmtService.refundGenerateOtp(orderId);

      if (result.success && result.data?.status === "SUCCESS") {
        setToken(result.data.token);
        setOtpTimer(30);
        setCanResendOtp(false);
        setOtp(["", "", "", "", "", ""]);
        // Focus first input
        const firstInput = document.getElementById("otp-input-0");
        if (firstInput) firstInput.focus();

        Swal.fire("Success!", "New OTP sent successfully", "success");
      } else {
        Swal.fire("Error!", result.message || "Failed to resend OTP", "error");
      }
    } catch (error) {
      Swal.fire("Error!", error.message || "Network error", "error");
    } finally {
      setIsGeneratingOtp(false);
    }
  };

  const resetOtpModal = () => {
    setOtp(["", "", "", "", "", ""]);
    setMobileNumber("");
    setOrderId("");
    setOtpTimer(0);
    setCanResendOtp(false);
  };

  const closeOtpModal = () => {
    setShowOtpModal(false);
    resetOtpModal();
  };

  const renderCell = (value) => (
    <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
      {value || "-"}
    </div>
  );

  const getStatusBadge = (status) => {
    switch (status?.toUpperCase()) {
      case "SUCCESS":
        return <span className="badge bg-success">{status}</span>;
      case "FAILED":
      case "REFUND":
        return <span className="badge bg-danger">{status}</span>;
      case "PENDING":
        return <span className="badge bg-warning">{status}</span>;
      case "REFUND_PENDING":
        return <span className="badge bg-info">{status}</span>;
      default:
        return <span className="badge bg-secondary">{status || "-"}</span>;
    }
  };

  const columns = [
    {
      name: "#",
      selector: (row, index) => (page - 1) * size + index + 1,
      width: "70px",
    },
    {
      name: "Date",
      selector: (row) => row.created_on,
      cell: (row) => renderCell(row.created_on),
      sortable: true,
    },
    {
      name: "Order ID",
      selector: (row) => row.order_id,
      cell: (row) => renderCell(row.order_id),
    },
    {
      name: "Sender Number",
      selector: (row) => row.optional1,
      cell: (row) => renderCell(row.optional1),
    },
    {
      name: "Bank Name",
      selector: (row) => row.optional3,
      cell: (row) => renderCell(row.optional3),
    },
    {
      name: "Beneficiary",
      cell: (row) =>
        renderCell(
          <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            <div>
              <strong>Name:</strong> {row.optional4}
            </div>
            <div>
              <strong>Account:</strong> {row.optional5}
            </div>
            <div>
              <strong>IFSC:</strong> {row.optional6}
            </div>
          </div>,
        ),
    },
    {
      name: "RRN",
      selector: (row) => row.operator_id,
      cell: (row) => renderCell(row.operator_id),
    },
    {
      name: "Transfer Amount",
      selector: (row) => `₹${row.order_amount || 0}`,
      cell: (row) => renderCell(`₹${row.order_amount || 0}`),
    },
    {
      name: "Status",
      cell: (row) => getStatusBadge(row.status),
      width: "120px",
    },
    {
      name: "Action",
      cell: (row) =>
        renderCell(
          <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            {(row.status === "REFUND_PENDING" ||
              row.secret_optional1 === "REFUND_PENDING") && (
              <button
                className="btn btn-primary btn-xs"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "0.15rem 0.4rem",
                  fontSize: "0.7rem",
                  whiteSpace: "nowrap",
                }}
                onClick={() => refundGenerateOtpHandler(row.order_id)}
                disabled={isGeneratingOtp}
              >
                {isGeneratingOtp ? "Generating..." : "Initiate Refund"}
              </button>
            )}
          </div>,
        ),
    },
  ];

  const customStyles = {
    table: {
      style: {
        minWidth: "1400px",
      },
    },
    headCells: {
      style: {
        padding: "6px",
        fontSize: "13px",
        fontWeight: "600",
        whiteSpace: "nowrap",
        backgroundColor: "#000",
        color: "#fff",
        borderBottom: "1px solid #444",
      },
    },
    cells: {
      style: {
        padding: "6px",
        fontSize: "13px",
        whiteSpace: "nowrap",
      },
    },
  };

  const handlePageChange = (page) => {
    setPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setSize(newPerPage);
    setPage(page);
  };

  // Modal styles
  const modalStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      width: "400px",
      maxWidth: "90%",
      borderRadius: "10px",
      border: "1px solid #ccc",
      boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
      padding: "20px",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 1000,
    },
  };

  return (
    <div className="container-fluid mt-3">
      {loading && <LoadingScreen />}

      <div className="row">
        <SideBar />
        <div className="col-md-9 mb-3">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title pb-2">Refund Eligible Transactions</h5>

              <div className="mb-3 text-end">
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  {showFilters ? "Hide Filters" : "Show Filters"}
                </button>
              </div>

              {showFilters && (
                <div className="ps-3 pe-3">
                  <div className="row mb-4 border p-3 rounded bg-light">
                    <div className="col-md-3">
                      <label>From Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={fromDate}
                        onChange={(e) => {
                          setFromDate(e.target.value);
                          setIsInitState(false);
                        }}
                      />
                    </div>
                    <div className="col-md-3">
                      <label>To Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={toDate}
                        onChange={(e) => {
                          setToDate(e.target.value);
                          setIsInitState(false);
                        }}
                      />
                    </div>
                    <div className="col-md-4">
                      <label>Search</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by Mobile, Email, Order ID, Ack No"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2 d-flex align-items-end">
                      <button
                        className="btn btn-primary w-100"
                        onClick={fetchData}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Scrollable Table */}
              <div style={{ overflowX: "auto", maxHeight: "70vh" }}>
                <DataTableComponent
                  columns={columns}
                  data={data}
                  progressPending={loading}
                  pagination
                  paginationServer
                  paginationTotalRows={totalRows}
                  paginationPerPage={size}
                  paginationDefaultPage={page}
                  onChangeRowsPerPage={handlePerRowsChange}
                  onChangePage={handlePageChange}
                  highlightOnHover
                  striped
                  persistTableHead
                  responsive={false}
                  noDataComponent="No records found"
                  customStyles={customStyles}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* OTP Verification Modal */}
      <Modal
        isOpen={showOtpModal}
        onRequestClose={closeOtpModal}
        style={modalStyles}
        contentLabel="OTP Verification"
      >
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Verify OTP</h5>
            <button
              type="button"
              className="btn-close"
              onClick={closeOtpModal}
              disabled={isVerifyingOtp}
            ></button>
          </div>
          <div className="modal-body">
            <div className="text-center mb-3">
              <p>Enter the 6-digit OTP sent to sender mobile number.</p>
            </div>

            <div className="d-flex justify-content-center mb-4">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-input-${index}`}
                  type="text"
                  maxLength="1"
                  className="form-control text-center mx-1"
                  style={{
                    width: "45px",
                    height: "45px",
                    fontSize: "18px",
                    fontWeight: "bold",
                  }}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  disabled={isVerifyingOtp}
                />
              ))}
            </div>

            <div className="text-center mb-3">
              {otpTimer > 0 ? (
                <p className="text-muted">
                  Resend OTP in <strong>{otpTimer}</strong> seconds
                </p>
              ) : canResendOtp ? (
                <p>
                  Didn't receive OTP?{" "}
                  <button
                    className="btn btn-link p-0"
                    onClick={resendOtpHandler}
                    disabled={isGeneratingOtp}
                  >
                    {isGeneratingOtp ? "Sending..." : "Resend OTP"}
                  </button>
                </p>
              ) : null}
            </div>

            <div className="d-grid gap-2">
              <button
                className="btn btn-primary"
                onClick={() => verifyOtpHandler()}
                disabled={isVerifyingOtp || otp.join("").length !== 6}
              >
                {isVerifyingOtp ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Verifying...
                  </>
                ) : (
                  "Verify & Process Refund"
                )}
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={closeOtpModal}
                disabled={isVerifyingOtp}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ClaimRefund;
